import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AWSOnPremisesSummary, AWSResidualCashFlow, TcoAssessmentSummaryOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Container, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import PlotlyBasic, { Data, Layout } from 'plotly.js-basic-dist';
import { createRef, useContext, useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import { match, useRouteMatch } from 'react-router';

import { AWS_ORANGE_COLOR, ON_PREMISES_COLOR } from '../../common/colors';
import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { formatCurrency, formatCurrencyAsString, getCurrencySymbol } from '../../common/LocalizationHelper';
import { useResize } from '../../common/resize-hook';
import { MatchParam } from '../../common/types';
import { commonGraphConfig } from '../../common/util';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { TcoContext } from '../../contexts/TcoContext';
import { getYearsObject, tcoSummaryUserFriendly, wrapStrong } from './TCOHelper';

const PlotlyChart = createPlotlyComponent(PlotlyBasic);


const tcoService = new TcoService();

type Props = FlashContextInterface;

function TcoSummary({flash}: Props) {
    const componentRef = createRef<HTMLDivElement>();
    const graphDimensions = useResize(componentRef);

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;
    
    const { panelHandler } = useContext(HelpPanelContext);
    const { assessment } = useContext(TcoContext);
    
    const [ loading, setLoading ] = useState(false);
    const [ tcoSummary, setTcoSummary ] = useState<TcoAssessmentSummaryOutput | null>(null);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.TCO_SUMMARY);
    },[ panelHandler ]);

    const loadSummaryData = async () => {
        try {
            setLoading(true);
            if (tcoAssessmentId && tcoAssessmentId !== 'default') {
                const summary = await tcoService.getSummary(tcoAssessmentId);
                setTcoSummary(summary.output);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_SUMMARY);
        } finally {
            setLoading(false);
        }
    };

    function buildTableHeader(item: Record<string,string> | AWSOnPremisesSummary, leftPadding?: JSX.Element[], rightPadding?: JSX.Element[], 
        className?: string, keysToExclude?: string[]) {
        const includedKeys = keysToExclude ? Object.keys(item).filter(item => !keysToExclude.includes(item)) : Object.keys(item);

        const headerRowContent = includedKeys.map((header: string, index) => {
            return (
                <th key={index}>
                    <Box textAlign='right'><span className={className}>{tcoSummaryUserFriendly(header)}</span></Box>
                </th>
            );
        });

        return (
            <tr>
                {leftPadding}
                {headerRowContent}
                {rightPadding}
            </tr>
        );
    };

    type Residual = AWSResidualCashFlow | {total: number};

    const isTotalKey = (key: string) => key === 'total';

    function buildCashFlowTableContent<T extends Record<string, unknown>>(items: T[], leftRowSpanHeader: string, residual: Residual, hideRowKey = false) {
        const currency = tcoSummary?.summary.currency;
        return items.map(item => {
            const numFields = Object.keys(item).length;
            const rowValues = Object.entries(item).map(([key, values], index) => {
                const leftRowValue = (index === 0) ? <td key='left-row' rowSpan={numFields}><strong>{leftRowSpanHeader}</strong></td> : null;
                if (Array.isArray(values)) {
                    return (
                        <tr key={index}>
                            {leftRowValue}
                            <td key='hide-key'>{hideRowKey ? '' : wrapStrong(tcoSummaryUserFriendly(key), isTotalKey(key))}</td>
                            {values.map((value, index) => {
                                return (
                                    <td key={index}>
                                        <Box textAlign='right'>
                                            {wrapStrong(formatCurrency(value, {maximumFractionDigits: 0, minimumFractionDigits: 0, currency}), isTotalKey(key))}
                                        </Box>
                                    </td>
                                );
                            })}
                            <td key='residual'>
                                <Box textAlign='right'>
                                    { residual[key as keyof Residual] || key === 'total' ?
                                        wrapStrong(
                                            formatCurrency(residual[key as keyof Residual], {maximumFractionDigits: 0, minimumFractionDigits: 0, currency}),
                                            isTotalKey(key))
                                        : null }
                                </Box>
                            </td>
                        </tr>
                    );
                }
                else {
                    return null;
                }
            });
            return rowValues;
        });
    };

    function buildCostBreakdownTableContent(items: AWSOnPremisesSummary[], leftRowSpanHeader: string, keysToExclude?: string[]) {
        const currency = tcoSummary?.summary.currency;
        return items.map((item, index) => {
            const includedEntries = keysToExclude ? Object.entries(item).filter(([key,]) => !keysToExclude.includes(key)) : Object.entries(item);

            const rowValues = includedEntries.map(([, field], rowIndex) => {
                return (
                    <td key={rowIndex}>
                        <Box textAlign='right'>
                            {formatCurrency(field, {maximumFractionDigits: 0, minimumFractionDigits: 0, currency})}
                        </Box>
                    </td>
                );
            });
            const leftRowValue = (index === 0) ? <td key='left-row' rowSpan={items.length}><strong>{leftRowSpanHeader}</strong></td> : <td key='left-row'/>;
            return (
                <tr key={`${leftRowSpanHeader}-${index}`}>
                    {leftRowSpanHeader ? (leftRowValue) : null}
                    {rowValues}
                </tr>
            );
        });
    }

    const buildCostBreakdownTable = () => {
        if (tcoSummary) {
            return (
                <Box className='scroll-horizontal'>
                    <table className='awsui data-table'>
                        <thead>
                            {buildTableHeader(tcoSummary.summary.aws,
                                [(<th key='empty-header-left'><span className='awsui-table-header-content'></span></th>)],
                                [], 'awsui-table-header-content', ['manualAdjustment', 'adjustedTotal'])}
                        </thead>
                        <tbody>
                            {buildCostBreakdownTableContent([tcoSummary.summary.aws], constants.AWS, ['manualAdjustment', 'adjustedTotal'])}
                            {buildCostBreakdownTableContent([tcoSummary.summary.onPremises], constants.ON_PREMISES, ['manualAdjustment', 'adjustedTotal'])}
                        </tbody>
                    </table>
                </Box>
            );
        }

        return null;
    };

    const buildCashFlowTable = () => {
        if (tcoSummary) {
            const leftRowSpanHeaders = [
                (
                    <th key='cash-flow'>
                        <span className='awsui-table-header-content'>{constants.CASH_FLOW_SUMMARY}</span>
                    </th>
                ), (
                    <th key='empty-left'>
                        <span className='awsui-table-header-content'></span>
                    </th>
                ),
            ];

            const rightRowSpanHeaders = [
                (
                    <th key='residual'>
                        <Box textAlign='right'>
                            <span className='awsui-table-header-content'>{constants.RESIDUAL_AND_UNUSED_VALUE}</span>
                        </Box>
                    </th>
                )
            ];

            const headerCellClassName = 'awsui-table-header-content';

            const awsCashflow = buildCashFlowTableContent(
                [tcoSummary.summary.cashFlow.aws.yearly],
                constants.AWS,
                tcoSummary.summary.cashFlow.aws.residual
            );

            const onPremCashflow = buildCashFlowTableContent(
                [tcoSummary.summary.cashFlow.onPremises.yearly],
                constants.ON_PREMISES,
                tcoSummary.summary.cashFlow.onPremises.residual
            );

            const differenceInCashflow = buildCashFlowTableContent(
                [tcoSummary.summary.cashFlow.delta.yearly],
                constants.GAIN_LOSS,
                tcoSummary.summary.cashFlow.delta.residual,
                true
            );

            return (
                <Box className='scroll-horizontal'>
                    <table className='awsui data-table'>
                        <thead>
                            {buildTableHeader(getYearsObject(tcoSummary), leftRowSpanHeaders, rightRowSpanHeaders, headerCellClassName)}
                        </thead>
                        <tbody>
                            {awsCashflow}
                            {onPremCashflow}
                            {differenceInCashflow}
                        </tbody>
                    </table>
                </Box>
            );
        }

        return null;
    };

    const AWSvsOnPremisesTotalLayout: Partial<Layout> = {
        barmode: 'stack',
        showlegend: false,
        height: 300,
        width: graphDimensions.width,
        margin: {
            l: 100
        },
        xaxis:{hoverformat: ',f'}, // ,f means show the interger number with thousands separator, see docs at link
        yaxis:{hoverformat: ',f'}  // https://github.com/d3/d3-format/blob/master/README.md#locale_formatPrefix
    };

    const graphDataAWSvsOnPremisesTotal = ({ summary }: TcoAssessmentSummaryOutput): Data[] => {
        const { currency } = summary;
        const item = 'adjustedTotal';
        const onPremCost = summary.onPremises[item as keyof AWSOnPremisesSummary] ?? 0;
        const awsCost = summary.aws[item as keyof AWSOnPremisesSummary] ?? 0;

        const data: Data[] = [
            {
                x: [onPremCost],
                y: [`${constants.ON_PREMISES} `],
                name: constants.ON_PREMISES,
                orientation: 'h',
                type: 'bar',
                marker: {color: ON_PREMISES_COLOR},
                hovertemplate: `${formatCurrencyAsString(onPremCost, {maximumFractionDigits: 0, minimumFractionDigits: 0, currency})}`
            },
            {
                x: [awsCost],
                y: [`${constants.AWS} `],
                name: constants.AWS,
                orientation: 'h',
                type: 'bar',
                marker: {color: AWS_ORANGE_COLOR},
                hovertemplate: `${formatCurrencyAsString(onPremCost, {maximumFractionDigits: 0, minimumFractionDigits: 0, currency})}`

            }
        ];
        
        return data;
    };

    const  AWSvsOnPremisesBreakdownLayout: Partial<Layout> = {
        barmode: 'group',
        height: 300,
        width: graphDimensions.width,
        xaxis:{hoverformat: ',f'}, // ,f means show the interger number with thousands separator, see docs at link
        yaxis:{hoverformat: ',f'}  // https://github.com/d3/d3-format/blob/master/README.md#locale_formatPrefix
    };

    const graphDataAWSvsOnPremisesBreakdown = ({ summary }: TcoAssessmentSummaryOutput): Data[] => {
        const { currency } = summary;
        // both sets of values are guaranteed to have the same keys
        const lineItems = Object.keys(summary.aws)
            .filter(item => !['total', 'manualAdjustment', 'adjustedTotal'].includes(item));

        const onPremisesValues = lineItems.map(function(item) {
            return summary.onPremises[item as keyof AWSOnPremisesSummary] ?? 0;
        });

        const awsValues = lineItems.map(function(item) {
            return summary.aws[item as keyof AWSOnPremisesSummary] ?? 0;
        });

        const lineItemsDisplay = lineItems.map(function(item){
            return tcoSummaryUserFriendly(item);
        });

        const onPremises: Data = {
            x: lineItemsDisplay, 
            y: onPremisesValues, 
            name: constants.ON_PREMISES, 
            type: 'bar',
            marker: {color: ON_PREMISES_COLOR},
            hovertemplate: `<b>%{x}:</b> ${getCurrencySymbol(currency)}%{y:,.0f}`
        };
            
        const aws: Data = {
            x: lineItemsDisplay, 
            y: awsValues, 
            name: constants.AWS, 
            type: 'bar',
            marker: {color: AWS_ORANGE_COLOR},
            hovertemplate: `<b>%{x}:</b> ${getCurrencySymbol(currency)}%{y:,.0f}`
        };

        const data = [aws, onPremises];
        
        return data;
    };

    const AWSvsOnPremisesCashFlowlayout: Partial<Layout> = {
        barmode: 'group',
        height: 300,
        width: graphDimensions.width,
        xaxis:{hoverformat: ',f'}, // ,f means show the interger number with thousands separator, see docs at link
        yaxis:{hoverformat: ',f'}  // https://github.com/d3/d3-format/blob/master/README.md#locale_formatPrefix
    };

    const graphDataAWSvsOnPremisesCashFlow = ({ summary }: TcoAssessmentSummaryOutput): Data[] => {
        const { currency } = summary;
        const columns: string[] = summary.cashFlow.aws.yearly.total.map((year, index) => `${constants.YEAR} ` + (index + 1));

        const onPremisesValues = summary.cashFlow.onPremises.yearly.total;
        const awsValues = summary.cashFlow.aws.yearly.total;

        const onPremises: Data = {
            x: columns, 
            y: onPremisesValues, 
            name: constants.ON_PREMISES,
            type: 'bar',
            marker: {color: ON_PREMISES_COLOR},
            hovertemplate: `<b>%{x}:</b> ${getCurrencySymbol(currency)}%{y:,.0f}`
        };
            
        const aws: Data = {
            x: columns, 
            y: awsValues, 
            name: constants.AWS, 
            type: 'bar',
            marker: {color: AWS_ORANGE_COLOR},
            hovertemplate: `<b>%{x}:</b> ${getCurrencySymbol(currency)}%{y:,.0f}`
        };
            
        const data = [aws, onPremises];

        return data;
    };

    useEffect(() => {
        if (assessment) {
            loadSummaryData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const pageContent = !loading ? tcoSummary && (
        <SpaceBetween size='xl'>
            <Header data-testid='summary-report-header' variant='h1'>{constants.SUMMARY_REPORT}</Header>
            <Header data-testid='aws-vs-onprem-header' variant='h2'>{constants.AWS_VS_ON_PREM_TOTAL}</Header>
            <div ref={componentRef}>
                <PlotlyChart data={graphDataAWSvsOnPremisesTotal(tcoSummary)} layout={AWSvsOnPremisesTotalLayout} config={commonGraphConfig} />
            </div>
            <Header data-testid='aws-vs-onprem-cost-breakdown-header' variant='h2'>{constants.AWS_VS_ON_PREM_COST_BREAKDOWN}</Header>
            <div ref={componentRef}>
                <PlotlyChart data={graphDataAWSvsOnPremisesBreakdown(tcoSummary)} layout={AWSvsOnPremisesBreakdownLayout} config={commonGraphConfig} />
            </div>
            {buildCostBreakdownTable()}
            <Header data-testid='aws-vs-onprem-cashflow-header' variant='h2'>{constants.AWS_VS_ON_PREM_CASH_FLOW}</Header>
            <div ref={componentRef}>
                <PlotlyChart data={graphDataAWSvsOnPremisesCashFlow(tcoSummary)} layout={AWSvsOnPremisesCashFlowlayout} config={commonGraphConfig} />
            </div>
            {buildCashFlowTable()}
        </SpaceBetween>
    ) : (
        <Spinner size='big' />
    );

    return (
        <Container>
            {pageContent}
        </Container>
    );
}

export default withFlashContext(TcoSummary);
